import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiClientError } from '@/lib/api';
import { useCurrentOrganizationId } from '@/features/organizations/store/useCurrentOrganizationStore';
import {
    getOnboardingState,
    updateOnboardingState,
    type OnboardingStateResponse,
    type OnboardingStateUpdateInput
} from '@/features/onboarding/services/onboarding';
import { onboardingKeys } from '@/features/onboarding/services/queryKeys';
import { syncOnboardingStoreFromServer } from '@/features/onboarding/store';
import { startSocialAccountConnect } from '@/features/social-accounts/services/social-accounts';

export function useOnboardingState(options?: { refetchInterval?: number | false }) {
    const organizationId = useCurrentOrganizationId();

    return useQuery({
        queryKey: organizationId ? onboardingKeys.state(organizationId) : onboardingKeys.all,
        queryFn: () => getOnboardingState(organizationId!),
        enabled: Boolean(organizationId),
        refetchInterval: options?.refetchInterval ?? false
    });
}

export function useUpdateOnboardingState(organizationId: string | null) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: onboardingKeys.updateStateMutation(organizationId),
        mutationFn: async (input: OnboardingStateUpdateInput) => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            return updateOnboardingState(organizationId, input);
        },
        onSuccess: (data: OnboardingStateResponse) => {
            if (!organizationId) {
                return;
            }

            queryClient.setQueryData(onboardingKeys.state(organizationId), data);
            syncOnboardingStoreFromServer(organizationId, data);
        }
    });
}

/**
 * Cache-derived pending state for onboarding-state updates. Unlike reading
 * `useMutation(...).isPending` from a hook instance, this subscribes to the
 * mutation cache directly, so it stays correct even when a hook-level
 * mutation observer detaches from an in-flight mutation (e.g. mutations
 * fired from mount effects under StrictMode remounts), which previously left
 * the checkout CTAs disabled until a full page reload.
 */
export function useIsUpdatingOnboardingState(organizationId: string | null): boolean {
    return useIsMutating({ mutationKey: onboardingKeys.updateStateMutation(organizationId) }) > 0;
}

/**
 * Starts the real Instagram OAuth flow with success/error redirects pointing
 * back at the onboarding connect step (the backend validates both URLs as
 * same-origin against its configured frontend callback URL).
 */
export function useStartOnboardingInstagramConnect(organizationId: string | null) {
    return useMutation({
        mutationFn: async () => {
            if (!organizationId) {
                throw new ApiClientError({ message: 'No organization selected', status: 400 });
            }

            const origin = window.location.origin;

            return startSocialAccountConnect({
                organizationId,
                provider: 'instagram',
                successRedirectUrl: `${origin}/onboarding/connect`,
                errorRedirectUrl: `${origin}/onboarding/connect`,
                source: 'onboarding'
            });
        }
    });
}
